/**
 * TokensApiClient
 * 
 * Handles token creation, redemption, and management
 */

import type { TokenPlatformPayload } from '@vaiu/types';
import { BaseApiClient } from './BaseApiClient';
import { RedeemInfo, TokenRedemptionResult } from '../../types/api/token';

export class TokensApiClient extends BaseApiClient {
  /**
   * Create a new token
   * 
   * @param tokenData.fundAsNeeded - If true, auto-charge default payment method if balance insufficient
   * 
   * Note: Bank user privileges (creating tokens without balance restrictions) should be 
   * handled by the backend checking user.roles.includes('BANK'), NOT via the fundAsNeeded flag.
   * The backend should:
   * - For regular users: Require sufficient balance OR fundAsNeeded=true with payment method
   * - For BANK role users: Allow negative balance regardless of fundAsNeeded flag
   */
  async createToken(tokenData: {
    amount: number;
    currency: string;
    message?: string;
    fundAsNeeded?: boolean;  // For auto-charging payment methods, NOT for bank privileges
    isReferralToken?: boolean;
    // Token validity: time window + documented redemption scope (redeemer / geo), not top-level restrictedTo*
    validity?: {
      start?: string;
      end?: string;
      duration?: number;
      redeemer?: {
        firstName?: string;
        lastName?: string;
        username?: string;
        telephone?: string;
        email?: string;
      };
      location?: { lat: number; lng: number; rad: number };
    };
    // Multi-use: set maxUses > 1 (deprecated `multiUse` field is rejected by the platform)
    maxUses?: number;
    maxAmountPerUse?: number;
    // Merchant targeting
    merchantId?: string;
    cartData?: {
      items: Array<{
        name: string;
        quantity: number;
        price: number;
      }>;
    };
    customerInfo?: {
      name?: string;
      email?: string;
      phone?: string;
    };
    // Fulfillment preferences
    fulfillmentPreferences?: {
      type: 'shipping' | 'digital' | 'pickup' | 'service';
      address?: string;
    };
    // Auto-deposit parameters
    recipientUserId?: string;  // User ID of the recipient (e.g., merchant)
    recipientEmail?: string;   // Email for recipient lookup (alternative to userId)
    recipientPhone?: string;   // Phone for recipient lookup (alternative to userId)
    autoProcess?: boolean;     // Enable auto-deposit (should be true for merchant payments)
    forceInbox?: boolean;      // Force to inbox even if auto-deposit is enabled (don't set for normal flow)
    /** Optional platform `payload` — see {@link TokenPlatformPayload}. Not for redemption rules; use `validity`. */
    payload?: TokenPlatformPayload;
  }): Promise<{
    key: string;
    tokenObj: {
      data: {
        amount: number;
        currency: string;
        message?: string;
        // Funding source tracking removed
      };
      status: string;
    };
    expiresAt: string;
    referralInfo?: {
      isReferralToken: boolean;
      rewardPotential: number;
      message: string;
    };
    // Auto-deposit response
    recipientDelivery?: {
      status: 'auto_deposited' | 'in_inbox' | 'recipient_not_found';
      message: string;
      transactionId?: string;
      newBalance?: number;
    };
  }> {
    console.log('🪙 Creating token with data:', tokenData);
    
    const response = await this.post<any>('/activate', tokenData);
    
    console.log('🪙 Token creation response:', response);
    return response;
  }

  /**
   * Create an identity token (ID token).
   * ID tokens provide temporary identity authentication - see API Reference Manual.
   * Key format: ID: + 6 alphanumeric chars (e.g. ID:Abc123).
   * Payload is flexible - only include fields with meaningful values; backend ignores unknown fields.
   */
  async createIdentityToken(tokenData: Record<string, unknown>): Promise<{
    key: string;
    type: string;
    amount: number;
    status: string;
    scope?: string[];
    canCredit?: string[];
    expiresAt: string;
    message?: string;
    expiresInHours?: number;
  }> {
    const response = await this.post<any>('/activate', {
      type: 'identity',
      ...tokenData,
    });
    return response;
  }

  /**
   * Update token conditions (only works if token hasn't been revealed yet)
   */
  async updateTokenConditions(tokenKey: string, conditions: {
    validity?: {
      start?: string;
      end?: string;
      duration?: number;
      redeemer?: {
        firstName?: string;
        lastName?: string;
        username?: string;
        telephone?: string;
        email?: string;
      };
      location?: { lat: number; lng: number; rad: number };
    };
    maxUses?: number;
    maxAmountPerUse?: number;
    /** Optional `payload` when the platform allows it — see {@link TokenPlatformPayload}. Use `validity` for redemption rules. */
    payload?: TokenPlatformPayload;
  }): Promise<{ success: boolean; message: string }> {
    return this.patch(`/tokens/${tokenKey}/conditions`, conditions);
  }

  /**
   * Reveal token metadata without redeeming (unified API: POST /api/v1/trigger, redeem: false).
   * @param transactor — Caller username or identifier (required by platform)
   */
  async revealToken(
    tokenKey: string,
    transactor: string,
    redeemAmount?: number
  ): Promise<{
    tokenType?: 'invoice' | 'cash';
    amount?: number;
    currency?: string;
    message?: string;
    conditions?: unknown;
    startDate?: string;
    endDate?: string;
    multiuse?: boolean;
    status?: string;
  }> {
    const t = transactor?.trim();
    if (!t) {
      throw new Error('revealToken requires transactor (caller username or identifier)');
    }
    const payload: Record<string, unknown> = {
      key: tokenKey,
      transactor: t,
      redeem: false,
    };
    if (redeemAmount !== undefined) {
      payload.amount = redeemAmount;
    }
    const raw = (await this.post<Record<string, unknown>>('/trigger', payload)) ?? {};
    const r = (raw.data ?? raw.result ?? raw) as Record<string, unknown>;
    const tt = r.tokenType;
    const tokenType: 'invoice' | 'cash' | undefined =
      tt === 'invoice' || tt === 'cash' ? tt : undefined;
    const status = typeof r.status === 'string' ? r.status : undefined;
    return {
      tokenType,
      amount: typeof r.amount === 'number' ? r.amount : undefined,
      currency: typeof r.currency === 'string' ? r.currency : undefined,
      message: typeof r.message === 'string' ? r.message : undefined,
      conditions: r.conditions,
      startDate: typeof r.startDate === 'string' ? r.startDate : undefined,
      endDate: typeof r.endDate === 'string' ? r.endDate : undefined,
      multiuse: Boolean(r.multiuse ?? r.multiUse),
      status,
    };
  }

  /**
   * Redeem a token
   * @param tokenKey - The token key to redeem
   * @param redeemAmount - Optional amount for partial redemption
   */
  async redeemToken(tokenKey: string, redeemAmount?: number): Promise<TokenRedemptionResult> {
    const payload: Record<string, unknown> = {
      key: tokenKey,
      transactor: 'balance',
      redeem: true,
    };
    if (redeemAmount !== undefined) {
      payload.amount = redeemAmount;
    }
    return this.post('/trigger', payload);
  }


  /**
   * Pay an invoice using a cash token
   * @param tokenKey - The cash token to use for payment
   * @param redeemInfo - Invoice payment information
   */
  async payInvoiceToken(tokenKey: string, redeemInfo: RedeemInfo): Promise<TokenRedemptionResult> {
    const payload: Record<string, unknown> = {
      key: tokenKey,
      transactor: 'balance',
      redeem: true,
      invoiceToken: redeemInfo.invoiceToken,
      message: redeemInfo.message,
    };

    return this.post('/trigger', payload);
  }

  /**
   * Get detailed token information
   */
  async getTokenDetails(tokenKey: string): Promise<{
    token: {
      key: string;
      amount: number;
      currency: string;
      status: string;
      message?: string;
      type: string;
      createdAt: string;
      updatedAt?: string;
      expiresAt?: string;
      completedAt?: string;
      redeemedAt?: string;
      createdBy?: string;
      redeemedBy?: string;
    };
    redemptionDetails?: {
      redeemedAt: string;
      redeemedBy: string;
      redeemedAmount: number;
      netAmount: number;
      fees: number;
      transactionId: string;
    };
    referralReward?: {
      isReferralToken: boolean;
      rewardAmount: number;
      rewardCurrency: string;
      rewardGiven: boolean;
      rewardGivenAt?: string;
      newUserReward: boolean;
    };
    invoicePayments?: Array<{
      paymentId: string;
      paidAt: string;
      paidBy: string;
      amount: number;
      currency: string;
      paymentMethod: string;
      transactionId: string;
    }>;
    prepaidCard?: {
      tokenId: string;
      uniqueUserId: string;
      cardNumber: string;
      cardExpMth: number;
      cardExpYear: number;
      cardCvv: number;
      cardHolderName: string;
      status: string;
      cardCurrency?: string;
      amountLoaded: number;
      currency: string;
      createdAt?: string;
      convertedAt?: string;
      cardBrand?: 'visa' | 'mastercard';
    };
  }> {
    return this.get(`/token-details/${tokenKey}`);
  }

  /**
   * Get invoice details by code
   */
  async getInvoiceDetails(invoiceCode: string): Promise<any> {
    return this.post('/invoice/details', { invoiceCode });
  }

  /**
   * Pay invoice with token
   */
  async payInvoice(paymentData: any): Promise<any> {
    return this.post('/pay-invoice', paymentData);
  }

  /**
   * Get user's tokens
   * NOTE: This endpoint (/user/tokens) does not exist in the backend.
   * Use the /api/v1/transactions endpoint instead which returns all transactions.
   * 
   * @deprecated This method references a non-existent endpoint - use transactions.getTransactions() instead
   */
  async getUserTokens(filters?: {
    status?: string;
    type?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    tokens: any[];
    total: number;
    hasMore: boolean;
  }> {
    // This endpoint doesn't exist - returning empty result to prevent 404 errors
    console.warn('getUserTokens called but /user/tokens endpoint does not exist. Use transactions.getTransactions() instead.');
    return {
      tokens: [],
      total: 0,
      hasMore: false
    };
  }

  /**
   * Cancel a token
   */
  async cancelToken(tokenKey: string): Promise<{ success: boolean; message: string }> {
    return this.post<{ success: boolean; message: string }>(`/tokens/${tokenKey}/cancel`);
  }

  /**
   * Extend token expiration
   */
  async extendTokenExpiration(tokenKey: string, newExpirationDate: string): Promise<{ success: boolean; message: string }> {
    return this.post<{ success: boolean; message: string }>(`/tokens/${tokenKey}/extend`, { 
      expirationDate: newExpirationDate 
    });
  }

  /**
   * Get token redemption history
   */
  async getTokenRedemptionHistory(tokenKey: string): Promise<{
    history: Array<{
      redeemedAt: string;
      redeemedBy: string;
      amount: number;
      currency: string;
      transactionId: string;
    }>;
  }> {
    return this.get<{
      history: Array<{
        redeemedAt: string;
        redeemedBy: string;
        amount: number;
        currency: string;
        transactionId: string;
      }>;
    }>(`/tokens/${tokenKey}/redemption-history`);
  }

  /**
   * Release escrow after an escrowed cash token was redeemed.
   * Documented platform path: POST /api/v2/escrow/release
   */
  async releaseEscrow(params: {
    tokenKey?: string;
    escrowId?: string;
    action?: 'release';
  }): Promise<{ success?: boolean; message?: string; [key: string]: unknown }> {
    return this.post('v2/escrow/release', {
      action: params.action ?? 'release',
      tokenKey: params.tokenKey,
      escrowId: params.escrowId,
    });
  }

  /**
   * Validate token before redemption
   */
  async validateToken(tokenKey: string): Promise<{
    valid: boolean;
    reason?: string;
    tokenInfo?: {
      amount: number;
      currency: string;
      type: string;
      status: string;
      expiresAt?: string;
    };
  }> {
    return this.get<{
      valid: boolean;
      reason?: string;
      tokenInfo?: {
        amount: number;
        currency: string;
        type: string;
        status: string;
        expiresAt?: string;
      };
    }>(`/tokens/${tokenKey}/validate`);
  }
} 
